/**
 * Top-level React error boundary.
 *
 * Wraps the lazy-loaded app shell so that an unhandled render error in
 * ScanPage, LeadsPage, SettingsPage, or any post-auth component lands on a
 * recoverable fallback UI instead of a blank white screen. At a live event
 * we cannot ship a vendor a debugger — the only acceptable failure mode is
 * "show what broke, give them a button to reload, and keep their queued
 * scans intact in IndexedDB".
 *
 * Behaviour:
 *  - On caught error: reports to Sentry (via the project's captureException
 *    helper, which is a no-op without a DSN) and renders a fallback card.
 *  - The "Reload app" button does a hard reload — the SW will pick up any
 *    pending update on the way back in. We deliberately do NOT clear local
 *    storage or the offline queue; those are the user's data.
 *  - In DEV the fallback also shows the error message + stack to make
 *    debugging quicker. Production hides it (no stack-leak in the UI).
 *
 * Why a class component:
 *   React only supports error boundaries via class lifecycle methods
 *   (`getDerivedStateFromError` + `componentDidCatch`). There is no hook
 *   equivalent as of React 19.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureException } from '@/lib/sentry';

interface Props {
	children: ReactNode;
	/**
	 * Optional descriptor for Sentry tagging. Lets us tell apart a crash in
	 * the auth gate from one inside the scanner shell. Default: 'unknown'.
	 */
	scope?: string;
}

interface State {
	error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
	state: State = { error: null };

	static getDerivedStateFromError(error: Error): State {
		return { error };
	}

	componentDidCatch(error: Error, info: ErrorInfo): void {
		captureException(error, {
			tags: { boundary: this.props.scope || 'unknown' },
			extra: { componentStack: info.componentStack || '' },
		});
	}

	private handleReload = (): void => {
		// Hard reload — picks up any pending SW update. The offline queue
		// and session state live in IndexedDB / localStorage and survive.
		window.location.reload();
	};

	render(): ReactNode {
		const { error } = this.state;
		if (!error) return this.props.children;

		const isDev = import.meta.env.DEV;
		return (
			<div
				role="alert"
				className="min-h-screen flex items-center justify-center px-6 py-10 bg-neutral-950 text-neutral-100"
			>
				<div className="max-w-md w-full rounded-2xl border border-neutral-800 bg-neutral-900 p-6 shadow-xl">
					<h1 className="text-xl font-semibold mb-2">Something went wrong.</h1>
					<p className="text-sm text-neutral-300 mb-4">
						The app hit an unexpected error. Your queued scans are safe — they're
						stored on this device and will sync when the app reloads.
					</p>
					{isDev ? (
						<pre className="mb-4 max-h-48 overflow-auto rounded-md bg-neutral-950 p-3 text-xs text-rose-300">
							{error.name}: {error.message}
							{'\n'}
							{error.stack}
						</pre>
					) : null}
					<button
						type="button"
						onClick={this.handleReload}
						className="btn-primary w-full min-h-[44px]"
						data-testid="button-errorboundary-reload"
					>
						Reload app
					</button>
				</div>
			</div>
		);
	}
}
